import { Accordion, Chip, Group, Stack, Text } from "@assetwatch/ui";
import type { ReactNode } from "react";
import { formatWorkOrderDueDate } from "../../../lib/formatWorkOrderDueDate";
import type { NavigationOrigin } from "../../../lib/navigationOrigin";
import type { WorkOrdersLocationSearch } from "../../../lib/workOrdersSearch";
import type { WorkOrderDetail } from "../../../queries/useWorkOrderDetailQuery";
import { workOrderPriorityTone, workOrderStatusTone } from "./presentation";
import { WorkOrderAssetLink } from "./WorkOrderAssetLink";
import {
  DetailCard,
  DetailGrid,
  Field,
  formatDate,
} from "./WorkOrderDetailLayout";

function hasDisplayValue(value: string | null | undefined) {
  return Boolean(value?.trim());
}

/**
 * Read-only Work Order record, split into the POC's card grouping
 * (`app/screens/wo-detail.jsx`'s Description / Status & Schedule / Work Info
 * + collapsible More Info; Jira IWA-20334 names the last card Work Information):
 * one focused card per concern instead of a single
 * flat grid, with secondary/source-synced fields tucked behind a disclosure.
 *
 * The POC's Attachments grid is passed in through `attachments` and rendered
 * as the last section of the Work Information card, matching the POC; it loads its
 * own data. The POC's "Asset Status" field is intentionally not built:
 * `WorkOrderDetail` has no asset-status data today (see
 * `useWorkOrderDetailQuery.ts`), so rendering it would mean fabricating data.
 */
export function WorkOrderOverview({
  attachments,
  descriptionActions,
  assigneesField,
  statusControl,
  workOrder,
  facilityTimeZone,
  originSearch,
  parentOrigin,
}: {
  /** Rendered as the last section of the Work Information card. */
  attachments?: ReactNode;
  descriptionActions?: ReactNode;
  /** The "Assigned to" field, shared with the Work Orders split view. */
  assigneesField?: ReactNode;
  /** Replaces the read-only status chip, e.g. with the status menu. */
  statusControl?: ReactNode;
  workOrder: WorkOrderDetail;
  facilityTimeZone?: string;
  originSearch?: WorkOrdersLocationSearch;
  parentOrigin?: NavigationOrigin;
}) {
  const statusTone = workOrderStatusTone[workOrder.status.code] ?? "neutral";
  const priorityTone = workOrder.priority
    ? (workOrderPriorityTone[workOrder.priority.code] ?? "neutral")
    : "neutral";

  return (
    <Stack gap={16}>
      <DetailCard actions={descriptionActions} id="details" title="Description">
        <div className="px-(--spacing-20) py-(--spacing-20)">
          <Text>{workOrder.description || "—"}</Text>
        </div>
      </DetailCard>

      <DetailCard title="Status & Schedule">
        <DetailGrid>
          <Field label="Status">
            {statusControl ?? (
              <Chip label={workOrder.status.name} tone={statusTone} />
            )}
          </Field>
          <Field label="Priority">
            <Chip label={workOrder.priority?.name ?? "—"} tone={priorityTone} />
          </Field>
          {workOrder.schedule.basicStartDate ? (
            <Field
              label="Start date"
              value={formatDate(workOrder.schedule.basicStartDate)}
            />
          ) : null}
          {workOrder.schedule.dueDate ? (
            <Field
              label="Due date"
              value={formatWorkOrderDueDate(
                workOrder.schedule.dueDate,
                facilityTimeZone,
              )}
            />
          ) : null}
          <Field
            label="Last updated"
            value={formatDate(workOrder.lifecycle.updatedOn)}
          />
        </DetailGrid>
        <div className="border-t border-outline-subtle px-(--spacing-20) py-(--spacing-20)">
          {assigneesField ?? (
            <Field
              label="Assigned to"
              value={workOrder.assignee?.name ?? "Unassigned"}
            />
          )}
        </div>
      </DetailCard>

      <DetailCard title="Work Information">
        <DetailGrid>
          {workOrder.asset ? (
            <Field label="Asset">
              <Group gap={8} wrap="wrap">
                <WorkOrderAssetLink
                  assetId={workOrder.asset.id}
                  label={workOrder.asset.name || workOrder.asset.code}
                  originSearch={originSearch}
                  parentOrigin={parentOrigin}
                  workOrderId={workOrder.id}
                />
                {workOrder.asset.name && workOrder.asset.code ? (
                  <Text tone="subtle">{workOrder.asset.code}</Text>
                ) : null}
              </Group>
            </Field>
          ) : null}
          {hasDisplayValue(workOrder.asset?.location) ? (
            <Field label="Location" value={workOrder.asset?.location} />
          ) : null}
          {workOrder.categories.length > 0 ? (
            <Field label="Categories">
              {/* @ds-deviation: the POC WoCategoryTag draws each category in its own palette color; the Category API has no color and the DS has no tag palette, so every category is a neutral Chip, and a retired one reads "(inactive)" — proposed resolution: promote a tag palette and a backend color field into the design system */}
              <Group gap={8} wrap="wrap">
                {workOrder.categories.map((category) => (
                  <Chip
                    key={category.id}
                    label={
                      category.active
                        ? category.name
                        : `${category.name} (inactive)`
                    }
                  />
                ))}
              </Group>
            </Field>
          ) : null}
        </DetailGrid>
        <div className="border-t border-outline-subtle">
          <Accordion title="More Info">
            {/* Not <DetailGrid>: its px/py bake in the standalone-card
                padding, which would stack on top of the Accordion's own
                "standard" p-(--spacing-12) shell padding. Only the grid
                layout is reused here; the Accordion owns the padding. */}
            <div className="grid grid-cols-1 gap-x-(--spacing-32) gap-y-(--spacing-20) sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Work type" value={workOrder.workType.name} />
              {hasDisplayValue(workOrder.plannerGroup?.name) ? (
                <Field
                  label="Planner group"
                  value={workOrder.plannerGroup?.name}
                />
              ) : null}
              {hasDisplayValue(workOrder.asset?.category) ? (
                <Field
                  label="Asset classification"
                  value={workOrder.asset?.category}
                />
              ) : null}
              {hasDisplayValue(workOrder.metadata.equipmentCode) ? (
                <Field
                  label="Equipment code"
                  value={workOrder.metadata.equipmentCode}
                />
              ) : null}
              {hasDisplayValue(workOrder.metadata.catalogProfile) ? (
                <Field
                  label="Work center"
                  value={workOrder.metadata.catalogProfile}
                />
              ) : null}
              <Field
                label="Created"
                value={`${workOrder.lifecycle.createdBy || "—"} · ${formatDate(workOrder.lifecycle.createdOn)}`}
              />
              {hasDisplayValue(workOrder.instructions) ? (
                <div className="sm:col-span-2 lg:col-span-3">
                  <Field label="Instructions" value={workOrder.instructions} />
                </div>
              ) : null}
            </div>
          </Accordion>
        </div>
        {attachments ? (
          <div className="border-t border-outline-subtle px-(--spacing-20) py-(--spacing-20)">
            {attachments}
          </div>
        ) : null}
      </DetailCard>
    </Stack>
  );
}
